import React, { useRef, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Box, Flex, Button, VisuallyHidden } from "@indeed/ifl-components";
import Play from "@indeed/ifl-icons/Play";
import Pause from "@indeed/ifl-icons/Pause";
import type { SystemStyleObject } from "@indeed/ifl-css";
import { colors } from "@indeed/ifl-tokens";
import { getIconColorStyles } from "helpers/getIconColorStyles";
import { usePrefersReducedMotion } from "hooks/usePrefersReducedMotion";

interface AnimationVideoProps {
  containerStyles?: SystemStyleObject;
  playButtonContainerStyles?: SystemStyleObject;
  url: string;
  videoElementStyles?: SystemStyleObject;
}

export const AnimationVideo: React.FC<AnimationVideoProps> = ({
  containerStyles,
  playButtonContainerStyles,
  url,
  videoElementStyles,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const animationRef = useRef<HTMLVideoElement>(null);
  const { ref, inView } = useInView({
    triggerOnce: true,
  });
  const [isPlaying, setIsPlaying] = useState(true);
  const [isLooping, setIsLooping] = useState(false);

  const play = () => {
    animationRef.current?.play();
    setIsPlaying(true);
  };

  const pause = () => {
    animationRef.current?.pause();
    setIsPlaying(false);
  };

  useEffect(() => {
    if (inView && !prefersReducedMotion) {
      play();
    } else {
      setIsPlaying(false);
    }
  }, [inView, prefersReducedMotion]);

  const playPause = () => {
    if (animationRef.current?.paused) {
      play();
      setIsLooping(true);
    } else {
      pause();
    }
  };

  const VideoIcon = isPlaying ? Pause : Play;

  return (
    <Box sx={{ position: "relative", ...containerStyles }} ref={ref}>
      <Box
        as="video"
        src={url}
        muted
        playsInline
        loop={isLooping}
        ref={animationRef}
        onEnded={() => setIsPlaying(false)}
        sx={{ display: "block", maxWidth: "100%", ...videoElementStyles }}
      />
      <Flex
        sx={{
          backgroundColor: "neutral.200",
          ...playButtonContainerStyles,
        }}
      >
        <Button
          variant="tertiary"
          sx={{ ...getIconColorStyles(colors.neutral["0"]) }}
          onClick={() => playPause()}
          iconOnly
        >
          <Box>
            <VisuallyHidden>
              {`${isPlaying ? "Pause" : "Play"} animation`}
            </VisuallyHidden>
          </Box>
          <VideoIcon sx={{ color: "primary.800" }} />
        </Button>
      </Flex>
    </Box>
  );
};
